import React, { LegacyRef, Ref, useEffect, useState } from 'react';
import { Hit } from '@algolia/client-search';

import { cn } from '@blockworks/ui/utils';

import SectionHeader from '@/components/display/headers/section-header';
import NewsSectionArticle from '@/components/pages/frontpage/components/news/news-section-article';
import { useAlgolia } from '@/context/algolia';
import { AlgoliaPostHit } from '@/types/algolia/algolia-post-hit';
import { ArticleType } from '@/types/article-type';
import mapAlgoliaHits from '@/utils/functions/algolia/map-algolia-hits';
import queryAlgoliaPosts from '@/utils/functions/algolia/query-algolia-posts';

type QuoteSectionProps = {
    className?: string;
    title?: string;
    linkUrl?: string;
    linkTitle?: JSX.Element | React.ReactNode | string;
    algoliaSearchKeyword?: string;
    algoliaFacetFilters?: string[][];
    algoliaRef?: LegacyRef<any> | Ref<any>;
    count?: number;
    page?: number;
    paddingClassName?: string;
    imageQuality?: number;
    style?: 'vertical' | 'horizontal';
};

const NewsRecirculationSection = (props: QuoteSectionProps) => {
    const {
        style,
        title,
        linkTitle,
        paddingClassName,
        linkUrl,
        count,
        page,
        className,
        algoliaSearchKeyword,
        algoliaRef,
        imageQuality,
        algoliaFacetFilters,
    } = props;
    const { algoliaKey } = useAlgolia();

    const [hits, setHits] = useState<ArticleType[]>(mapAlgoliaHits([]));

    // Fetch associated news articles through algolia
    useEffect(() => {
        (async () => {
            if (algoliaKey) {
                // Show most recent news if algolia search keyword is empty
                const term = algoliaSearchKeyword ?? '';

                try {
                    const queryResults = await queryAlgoliaPosts(
                        term ?? '',
                        count ?? 8,
                        page ?? 0,
                        {
                            proxy: true,
                            ...(algoliaFacetFilters ? { facetFilters: algoliaFacetFilters } : {}),
                        },
                        algoliaKey,
                    );
                    setHits(mapAlgoliaHits(queryResults.hits as Array<Hit<AlgoliaPostHit>>));
                } catch (err) {
                    console.error('Failed to fetch articles:', err);
                }
            }
        })();
    }, [algoliaFacetFilters, algoliaKey, algoliaSearchKeyword, count, page]);

    return (
        <section className={className}>
            {title && (
                <SectionHeader className="border-b border-t" title={title} linkTitle={linkTitle} linkUrl={linkUrl} />
            )}
            <div className={paddingClassName || 'p-12'} ref={algoliaRef}>
                <div
                    className={cn(
                        'grid gap-8',
                        style === 'vertical'
                            ? `grid-rows-1 xs:grid-rows-2 ${count === 3 && 'md:grid-rows-3'} ${
                                  Number(count) > 3 && 'lg:grid-rows-4'
                              }`
                            : `grid-cols-1 xs:grid-cols-2 ${count === 3 && 'md:grid-cols-3'} ${
                                  Number(count) > 3 && 'lg:grid-cols-4'
                              }`,
                    )}
                >
                    {hits?.map(article => (
                        <NewsSectionArticle
                            article={article}
                            key={`NewsSectionArticle-${article.slug}`}
                            imageQuality={imageQuality}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default NewsRecirculationSection;
